import { FC, Fragment } from 'react';

import {
  Alert,
  Box,
  Chip,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import Image from 'next/image';
import { mealDetailsModalShelfLifeDisplayInfoMap } from 'src/ts/enums/tags.enums';

import { MacroNutrients } from '../../ts/@types/generated/graphql/catalog-service/graphql';
import ShelfLifeIcon from '../Icons/ShelfLifeIcon';
import NutritionFactPanel from './NutritionFactPanel';
import BoldPrimaryIngredients from './BoldPrimaryIngredients';

export interface MealDetailsContentProps {
  title: string;
  subtitle?: string;
  description: string;
  macroNutrients: MacroNutrients;
  servingsTag?: { displayName: string } | null;
  nutritionCaveat?: string | null;
  publicIngredientsCopy: string;
  containsAllergensCopy: string | null;
  imageUrl: string;
  dietaryTemplateDisplayNames: string[];
  isNaeProteinSourcingStandard: boolean;
  shelfLifeTagId: number | undefined;
  kosherTagDisplayName?: string | null;
}

const MealDetailsContent: FC<MealDetailsContentProps> = ({
  containsAllergensCopy,
  description,
  dietaryTemplateDisplayNames,
  macroNutrients,
  servingsTag,
  publicIngredientsCopy,
  nutritionCaveat,
  title,
  subtitle,
  imageUrl,
  kosherTagDisplayName,
  isNaeProteinSourcingStandard,
  shelfLifeTagId,
}) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );

  //todo: once we've stabilized updating allergens (there was an incident 10/14/22) and changes are coming from BE, remove this.
  const formattedAllergensCopy = containsAllergensCopy
    ?.split(', ')
    ?.map((ita) => {
      if (ita?.includes('at all')) {
        const formattedIta = ita.split(' ');
        return formattedIta[0];
      }

      return ita;
    })
    ?.join(', ');

  // Splits on "CONTAINS", as it will be on its own line when present
  const [ingredients, allergensCopy] =
    publicIngredientsCopy.split(/CONTAINS:/i);

  const servingsPerContainer = servingsTag
    ? parseInt(servingsTag.displayName.split(' ')[0])
    : 1;

  const mealTitle = `${title} ${subtitle}`;

  return (
    <>
      {isSmallScreen ? (
        <Box
          sx={{
            height: '12rem',
            position: 'relative',
            mb: '0.75rem',
          }}
        >
          <Image
            src={imageUrl}
            alt={mealTitle}
            fill
            style={{ objectFit: 'cover', borderRadius: '2px 0 2px' }}
          />
        </Box>
      ) : undefined}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          '& > * + *': { borderLeft: '1px solid #DAD4BC' },
        }}
      >
        <Box
          sx={{
            width: isSmallScreen ? '100%' : '50%',
          }}
        >
          {isSmallScreen ? undefined : (
            <Box
              sx={{
                height: '12rem',
                position: 'relative',
                mb: '0.75rem',
              }}
            >
              <Image
                src={imageUrl}
                alt={mealTitle}
                fill
                style={{ objectFit: 'cover', borderRadius: '2px 0 2px' }}
              />
            </Box>
          )}

          <Box
            sx={{
              px: '1rem',
            }}
          >
            <Box>
              <NutritionFactPanel
                nutritional={{
                  ...macroNutrients,
                  servingsPerContainer,
                }}
              />
            </Box>
            <Box>
              {nutritionCaveat ? (
                <Fragment>
                  <Box sx={{ margin: '0.75rem 0' }}>
                    <Typography variant={'body2'}>
                      Notes: {nutritionCaveat}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: theme.palette.divider }} />
                </Fragment>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: isSmallScreen ? '100%' : '50%',
            padding: '1rem',
          }}
        >
          <Box sx={{ marginBottom: '0.75rem' }}>{description}</Box>

          <Box sx={{ marginBottom: '0.75rem' }}>
            {shelfLifeTagId ? (
              <Alert
                sx={{
                  borderColor: theme.palette.grey[300],
                  borderRadius: '14px',
                  margin: 0,
                  color: theme.palette.text.primary,
                }}
                variant={'outlined'}
                icon={
                  <ShelfLifeIcon
                    shelfLifeTagId={shelfLifeTagId}
                    color={theme.palette.primary.main}
                  />
                }
              >
                <Typography variant={'body2'} sx={{ fontWeight: 700 }}>
                  {mealDetailsModalShelfLifeDisplayInfoMap[shelfLifeTagId]
                    ?.title || ''}
                  {mealDetailsModalShelfLifeDisplayInfoMap[shelfLifeTagId]
                    ?.subtitle ? (
                    <Typography
                      component={'span'}
                      variant={'caption'}
                      sx={{ color: 'text.secondary', fontWeight: 600 }}
                    >
                      {' · '}
                      {
                        mealDetailsModalShelfLifeDisplayInfoMap[shelfLifeTagId]
                          ?.subtitle
                      }
                    </Typography>
                  ) : null}
                </Typography>
                <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                  {mealDetailsModalShelfLifeDisplayInfoMap[shelfLifeTagId]
                    ?.copy || ''}
                </Typography>
              </Alert>
            ) : undefined}
          </Box>

          {!!dietaryTemplateDisplayNames?.length ? (
            <Box sx={{ marginBottom: '0.75rem' }}>
              <Typography
                variant={'subtitle1'}
                sx={{
                  color: 'text.secondary',
                }}
              >
                Tags
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  rowGap: '0.5rem',
                }}
              >
                {dietaryTemplateDisplayNames.map(
                  (dietaryTemplateDisplayName) => {
                    return (
                      <Chip
                        key={dietaryTemplateDisplayName}
                        label={dietaryTemplateDisplayName}
                        sx={{
                          marginRight: '0.5rem',
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.25,
                          )}`,
                        }}
                      />
                    );
                  },
                )}
              </Box>
            </Box>
          ) : null}

          {isNaeProteinSourcingStandard ||
          kosherTagDisplayName ||
          formattedAllergensCopy ? (
            <Box sx={{ marginBottom: '0.75rem' }}>
              <Typography
                variant={'subtitle1'}
                sx={{
                  color: 'text.secondary',
                }}
              >
                Sourcing
              </Typography>
              {isNaeProteinSourcingStandard ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: '14px',
                    padding: '9px 12px',
                    marginBottom: '0.5rem',
                  }}
                >
                  <Image
                    src={`/static/images/nae-protein-sourcing.svg`}
                    height={22}
                    width={22}
                    alt="No antibiotics in protein icon"
                  />
                  <Typography variant="caption">
                    This meal has no antibiotics ever in the animal proteins.
                  </Typography>
                </Box>
              ) : null}
              {kosherTagDisplayName ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: '14px',
                    padding: '9px 12px',
                    marginBottom: '0.5rem',
                  }}
                >
                  <Typography variant="caption">
                    This meal is certified Kosher by {kosherTagDisplayName}
                  </Typography>
                </Box>
              ) : null}
              {formattedAllergensCopy ? (
                <Alert
                  severity={'warning'}
                  variant={'outlined'}
                  sx={{ borderRadius: '14px' }}
                >
                  Contains {formattedAllergensCopy}.
                </Alert>
              ) : null}
            </Box>
          ) : null}

          <Box>
            <Typography
              variant={'subtitle1'}
              sx={{
                color: 'text.secondary',
              }}
            >
              Ingredients
            </Typography>
            <Box
              sx={{
                borderRadius: '14px',
                border: '1px solid #DAD4BC',
                padding: '14px 16px',
              }}
            >
              <Typography variant={'caption'}>
                <BoldPrimaryIngredients ingredients={ingredients} /> <br />
                {allergensCopy ? (
                  <>
                    <strong>CONTAINS:</strong> {allergensCopy}
                  </>
                ) : null}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MealDetailsContent;
